import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Paperclip, X, Loader2, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export default function AddTaskDialog({ open, onOpenChange, onCreated }) {
    const [text, setText] = useState('');
    const [hasDate, setHasDate] = useState(false);
    const [dueDate, setDueDate] = useState('');
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [assignableUsers, setAssignableUsers] = useState([]);
    const [assignedTo, setAssignedTo] = useState([]); // array of user IDs
    const [assigneeFilter, setAssigneeFilter] = useState('');
    const fileInputRef = useRef(null);

    // Fetch the list of users that can be assigned each time the dialog opens
    // (lightweight, no admin role required).
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API_URL}/api/users/assignable`, { withCredentials: true });
                if (!cancelled) setAssignableUsers(res.data || []);
            } catch {
                /* silently ignore — the field stays empty, task can still be created without assignee */
            }
        })();
        return () => { cancelled = true; };
    }, [open]);

    const reset = () => {
        setText('');
        setHasDate(false);
        setDueDate('');
        setFile(null);
        setAssignedTo([]);
        setAssigneeFilter('');
    };

    const handleClose = (value) => {
        if (saving) return;
        if (!value) reset();
        onOpenChange(value);
    };

    const toggleAssignee = (uid) => {
        setAssignedTo(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
    };

    const handleSubmit = async () => {
        if (!text.trim()) {
            toast.error('Veuillez saisir le texte de la tâche');
            return;
        }
        if (hasDate && !dueDate) {
            toast.error('Veuillez choisir une date');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                text: text.trim(),
                due_date: hasDate ? dueDate : null,
                assigned_to: assignedTo,
            };
            if (file) {
                payload.file_data = await fileToBase64(file);
                payload.file_name = file.name;
            }
            await axios.post(`${API_URL}/api/tasks/manual`, payload, { withCredentials: true });
            toast.success(assignedTo.length ? `Tâche assignée à ${assignedTo.length} utilisateur${assignedTo.length > 1 ? 's' : ''}` : 'Tâche ajoutée (visible par tous)');
            reset();
            onOpenChange(false);
            onCreated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSaving(false);
        }
    };

    const filteredUsers = assigneeFilter.trim()
        ? assignableUsers.filter(u => (u.name || '').toLowerCase().includes(assigneeFilter.toLowerCase()) || (u.email || '').toLowerCase().includes(assigneeFilter.toLowerCase()))
        : assignableUsers;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="add-task-dialog">
                <DialogHeader className="px-6 pt-6 pb-2">
                    <DialogTitle className="text-lg font-bold text-[#1B365D]">Ajouter une tâche</DialogTitle>
                </DialogHeader>
                <div className="px-6 py-4 space-y-5 max-h-[65vh] overflow-y-auto">
                    <div>
                        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">Tâche à effectuer</label>
                        <Textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Ex : Rappeler Monsieur Dupont pour confirmer le devis…"
                            rows={3}
                            className="resize-none text-sm"
                            data-testid="task-text-input"
                            autoFocus
                        />
                    </div>

                    {/* Assignation utilisateurs */}
                    <div>
                        <div className="flex items-center gap-2 mb-1.5">
                            <Users className="w-3.5 h-3.5 text-[#C9A962]" />
                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                Assigner à
                            </label>
                            <span className="text-[11px] text-gray-400 italic">— laisser vide = visible par tous</span>
                        </div>
                        {assignableUsers.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">Aucun utilisateur disponible pour l'assignation.</p>
                        ) : (
                            <>
                                <Input
                                    value={assigneeFilter}
                                    onChange={(e) => setAssigneeFilter(e.target.value)}
                                    placeholder="Rechercher un utilisateur…"
                                    className="text-sm mb-2"
                                    data-testid="task-assignee-filter"
                                />
                                <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100" data-testid="task-assignee-list">
                                    {filteredUsers.length === 0 ? (
                                        <p className="text-xs text-gray-400 italic px-3 py-2">Aucun résultat.</p>
                                    ) : (
                                        filteredUsers.map(u => {
                                            const checked = assignedTo.includes(u.id);
                                            return (
                                                <label
                                                    key={u.id}
                                                    className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors text-sm ${checked ? 'bg-[#C9A962]/10' : 'hover:bg-gray-50'}`}
                                                    data-testid={`task-assignee-item-${u.id}`}
                                                >
                                                    <Checkbox
                                                        checked={checked}
                                                        onCheckedChange={() => toggleAssignee(u.id)}
                                                        data-testid={`task-assignee-checkbox-${u.id}`}
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <p className="font-medium text-gray-700 truncate">{u.name || u.email}</p>
                                                        <p className="text-[11px] text-gray-400 truncate">{u.role}{u.email ? ` · ${u.email}` : ''}</p>
                                                    </div>
                                                </label>
                                            );
                                        })
                                    )}
                                </div>
                                {assignedTo.length > 0 && (
                                    <p className="text-[11px] text-[#C9A962] mt-1.5 font-semibold">
                                        {assignedTo.length} utilisateur{assignedTo.length > 1 ? 's' : ''} sélectionné{assignedTo.length > 1 ? 's' : ''}
                                    </p>
                                )}
                            </>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                                checked={hasDate}
                                onCheckedChange={(v) => setHasDate(!!v)}
                                data-testid="task-has-date-checkbox"
                            />
                            <span className="text-sm text-gray-700">Définir une date d'échéance</span>
                        </label>
                        {hasDate && (
                            <Input
                                type="date"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                className="w-full text-sm"
                                data-testid="task-due-date-input"
                            />
                        )}
                    </div>

                    <div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            data-testid="task-file-input"
                        />
                        {file ? (
                            <div className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2 min-w-0">
                                    <Paperclip className="w-4 h-4 text-[#C9A962] shrink-0" />
                                    <span className="text-sm text-gray-700 truncate">{file.name}</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setFile(null)}
                                    className="text-gray-400 hover:text-red-500 shrink-0"
                                    data-testid="task-file-remove"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full gap-2 border-dashed"
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="task-add-file-btn"
                            >
                                <Paperclip className="w-4 h-4" />
                                Ajouter un fichier
                            </Button>
                        )}
                    </div>
                </div>

                <DialogFooter className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-row gap-2 sm:justify-end">
                    <Button
                        variant="outline"
                        onClick={() => handleClose(false)}
                        disabled={saving}
                        data-testid="task-cancel-btn"
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={saving}
                        className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white gap-2"
                        data-testid="task-submit-btn"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Valider
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
